/* eslint-disable @typescript-eslint/no-dynamic-delete */
import { FaSortUp, FaSortDown, FaSort } from '@meronex/icons/fa';
import React, { CSSProperties } from 'react';
import { shallowEqual } from 'react-redux';
import { Box } from '@apia/theme';
import {
  EventEmitter,
  TMap,
  debugDispatcher,
  getSpecificParent,
  isChild,
  persistentStorage,
} from '@apia/util';
import { AutoEllipsis, useOtherTagButton } from '@apia/components';
import { domPropsSelector } from './ResponsiveTable';
import { useResponsiveTableContext } from './ResponsiveTableContext';
import { TResponsiveTableSortValue } from './types';
import { useResponsiveTable } from './store';
import { useDomProps } from './store/selection/useDomProps';

interface IHeaderCell {
  /**
   * Para ordenar en Apia se usa un índice de columna, que se
   * corresponde con su posición en base 0, sin tomar en cuenta
   * las columnas adicionales.
   */
  apiaColumnIndex: number;
  columnIndex: number;
  inTableColumnIndex: number;
}

const widthStorageName = 'tablesWidths';

/**
 * Calcula el ancho de la columna dependiendo del valor establecido,
 * si el valor es numérico o un string que contiene un número sin
 * unidad, devuelve el doble de ese número para establecer una
 * relatividad con los tamaños de las columnas actuales en Apia.
 *
 * Es decir, como todo en Apia era más chico, los tamaños que se
 * establecían antes no quedan visualmente agradables con los
 * tamaños actuales, por eso se magnifican multiplicando por 2.
 */
function getColumnWidth(width?: string | number) {
  if (typeof width === 'string') {
    if (width.match(/(%|px|vw|vh|vmin|vmax|cm|mm|in|pt|pc)/)) return width;
    if (width === '') return 'auto';
    const numberValue = Number.parseInt(width, 10);
    if (isNaN(numberValue)) return 'auto';
    return numberValue;
  }
  if (typeof width === 'number') return width;
  return width;
}
debugDispatcher.on(
  'cleanTableSizes',
  ([tableName]) => {
    const currentWidths = persistentStorage[widthStorageName] as TMap<unknown>;
    delete currentWidths[tableName as string];
    persistentStorage[widthStorageName] = currentWidths;
  },
  'Se usa para borrar el estado de una tabla. Uso: dd.cleanTableSizes("TableName")',
);
function makeColumnName(tableName: string, columnName: string): string {
  return `${tableName}${columnName}`;
}

const thWidthEmitter = new (class ThWidthEmitter extends EventEmitter<{
  resize: { name: string; width: number };
}> {})();

const NoMemoHeaderCell = ({
  apiaColumnIndex,
  columnIndex,
  inTableColumnIndex,
}: IHeaderCell) => {
  const { name, onSortChange } = useResponsiveTableContext();
  const uniqueId =
    (window.location.href.match(/busEntId=(\d+)/)?.[1] as string) ??
    (window.location.href.match(/query=(\d+)/)?.[1] as string) ??
    '';
  const isGlobal = window.isGlobal ? 'isGlobal' : 'notGlobal';
  const actualName = name + uniqueId + String(isGlobal);
  const columns = useResponsiveTable((global) => {
    const tableState = global.responsiveTableSlice[name];
    return tableState?.columns;
  }, shallowEqual);
  const column = columns[columnIndex];
  const additionalInfoColumns = columns.filter((c) => c.showAsAdditional);

  const storedWidth = (
    (persistentStorage[widthStorageName] as TMap<unknown>)?.[
      actualName
    ] as TMap<unknown>
  )?.[column.name] as number;

  const [width, setWidth] = React.useState(storedWidth ?? column.width ?? 200);
  const [isResize, setIsResize] = React.useState(storedWidth !== undefined);

  React.useEffect(() => {
    return void thWidthEmitter.on('resize', (ev) => {
      if (ev.name === makeColumnName(actualName, column.name)) {
        setIsResize(true);
        setWidth(ev.width);
      }
    });
  }, [column.name, actualName]);

  const isFilterRequired = useResponsiveTable((global) => {
    const tableState = global.responsiveTableSlice[name];
    const columnFilter = tableState?.filters.find(
      (current) => current.column === column.title,
    );
    return columnFilter?.required;
  }, shallowEqual);

  const isSortingAllowed = useResponsiveTable((global) => {
    const tableState = global.responsiveTableSlice[name];
    return tableState?.allowSorting;
  });

  const domProps = useDomProps(
    'th',
    {
      columnIndex: inTableColumnIndex,
    },
    domPropsSelector(name),
  );

  const sx: CSSProperties = React.useMemo(
    () =>
      !isResize
        ? {
            minWidth: getColumnWidth(column.minWidth ?? column.width),
            maxWidth:
              column.maxWidth !== undefined
                ? getColumnWidth(column.maxWidth)
                : undefined,
            width: getColumnWidth(column.width),
            position: 'relative',
          }
        : {
            width: `${width}px`,
            minWidth: `${width}px`,
            position: 'relative',
          },
    [column.maxWidth, column.minWidth, column.width, isResize, width],
  );

  const buttonProps = useOtherTagButton(
    React.useMemo(
      () => ({
        className: 'headButton',
        onClick: (ev) => {
          if (
            isChild(ev.target as HTMLElement, (current) =>
              current.classList.contains('headButton__resizer'),
            )
          )
            return;

          if (
            onSortChange &&
            column.allowSorting !== false &&
            isSortingAllowed !== false
          )
            onSortChange({
              column,
              columnIndex: apiaColumnIndex,
              name: column.name,
              sortValue: ['Asc', 'Desc'][
                column.currentSorting !== 'A' ? 0 : 1
              ] as TResponsiveTableSortValue,
            });
        },
      }),
      [apiaColumnIndex, column, isSortingAllowed, onSortChange],
    ),
  );
  return (
    <Box
      as="th"
      style={sx}
      data-columnname={column.name}
      title={column.title ?? column.label ?? column.name}
      {...buttonProps}
      {...domProps}
      role="rowheader"
      className={`${
        isFilterRequired && window.SHOW_REQUIRED_POSITION
          ? 'requiredFilterColumn'
          : ''
      }`}
      ref={(el) => {
        if (!column?.autoAdjustWidth) return;
        if (
          el instanceof HTMLElement &&
          !el.dataset.resized &&
          storedWidth === undefined
        ) {
          const clone = el.cloneNode(true) as HTMLElement;
          const child = clone.querySelector(
            '.headButton__label',
          ) as HTMLElement;
          child.style.opacity = '0';
          document.body.append(child);
          child.style.position = 'fixed';
          child.style.maxWidth = '0';
          child.style.minWidth = '0';
          child.style.width = '0';
          child.style.overflow = 'auto';
          child.style.textWrap = 'nowrap';

          window.requestAnimationFrame(() => {
            const idealWidth = child.scrollWidth;
            const currentWidth = el
              .querySelector('.headButton__label')!
              .getBoundingClientRect().width;
            const elWidth = el.getBoundingClientRect().width;
            const diff = idealWidth - currentWidth;
            setWidth(elWidth + diff + 5);

            el.dataset.resized = 'true';
          });
        }
      }}
    >
      <Box as="span" className="headButton__container">
        <Box
          as="span"
          className={`${
            isFilterRequired ? 'requiredFilter__Column' : ''
          } headButton__label`}
        >
          <AutoEllipsis
            overrideStyles={(el) => {
              const th = el.closest('th') as HTMLElement;
              return {
                width: `${Math.max(
                  100,
                  Number.parseInt(window.getComputedStyle(th).width),
                )}px`,
              };
            }}
          >
            {column.label}
          </AutoEllipsis>
        </Box>
        <Box as="span" className="headButton__sortIcon">
          {column.allowSorting !== false && column.currentSorting === 'A' && (
            <FaSortUp />
          )}
          {column.allowSorting !== false && column.currentSorting === 'D' && (
            <FaSortDown />
          )}
          {column.allowSorting !== false && !column.currentSorting && (
            <FaSort />
          )}
        </Box>
        <Box
          // sx={{ display: 'none' }}
          className="headButton__resizer"
          onMouseDown={(ev) => {
            if (!isResize) {
              const trElement = getSpecificParent(
                ev.target as HTMLElement,
                (current) => current.tagName === 'TR',
              );

              trElement?.querySelectorAll('th').forEach((current) => {
                thWidthEmitter.emit('resize', {
                  name: makeColumnName(
                    actualName,
                    current.dataset.columnname as string,
                  ),
                  width: (current as HTMLElement).clientWidth,
                });
              });

              setIsResize(true);
            }

            const th = getSpecificParent(
              ev.target as HTMLElement,
              (current) => current.tagName === 'TH',
            );
            if (!th) throw new Error('There is no th');

            const initialX = ev.clientX;
            const initialWidth = th.clientWidth;

            const resize = (mousemoveEv: MouseEvent) => {
              const dif = mousemoveEv.clientX - initialX;

              setWidth(initialWidth + dif);
            };
            const unsuscribe = () => {
              const trElement = getSpecificParent(
                ev.target as HTMLElement,
                (current) => current.tagName === 'TR',
              );
              trElement?.querySelectorAll('th').forEach((current) => {
                persistentStorage[widthStorageName] = {
                  ...(persistentStorage[widthStorageName] as TMap<unknown>),
                  [actualName]: {
                    ...((
                      persistentStorage[widthStorageName] as TMap<unknown>
                    )?.[actualName] as TMap<unknown>),
                    [current.dataset.columnname as string]: (
                      current as HTMLElement
                    ).clientWidth,
                  },
                };
              });
              //add additional info columns to map with an default with of 100
              additionalInfoColumns.forEach((c) => {
                persistentStorage[widthStorageName] = {
                  ...(persistentStorage[widthStorageName] as TMap<unknown>),
                  [actualName]: {
                    ...((
                      persistentStorage[widthStorageName] as TMap<unknown>
                    )?.[actualName] as TMap<unknown>),
                    [c.name]: 100,
                  },
                };
              });
              document.removeEventListener('mousemove', resize);
              document.removeEventListener('mouseup', unsuscribe);
            };

            document.addEventListener('mousemove', resize);
            document.addEventListener('mouseup', unsuscribe);

            return false;
          }}
        />
      </Box>
    </Box>
  );
};

export const HeaderCell = React.memo(NoMemoHeaderCell);
